import axios, { AxiosInstance, AxiosResponse } from "axios";
import { Committee } from "Stores/committeeStore";
import { Dashboard } from "Stores/dashboardStore";
import { Comment } from "Stores/dashboardStore/comments";
import { Student, TableParams } from "Stores/commonStore";
import { FeaturesTableParams } from "Stores/featureGating";
import { Utils } from "../common/utils";
import { PostscriptCategory, PostscriptTerm } from "Stores/superStore";
import { EnfluxTerm } from "Stores/superStore/ingestion";

import { saveAs } from "file-saver";

import CONFIG from "Config";

import printJS from "print-js";
import { LabelGroup } from "Stores/labelStore";
import { AttachmentFile } from "Components/Common/AttachmentSelector";
import { StudentGroup } from "Stores/studentStore";
import { dashboardOrder } from "Components/Dashboard/list";
import { Banner } from "Stores/bannerStore";
import { CustomOptionType, CourseStatus, CoursesCatalogResponse, CurriculumMapping, DomainCompetency, SectionsCatalogResponse, CourseDescription, CustomDomainCompetency } from "Stores/courseStore";
import { CreateCourseRequest, EditCourseRequest, SectionsIds } from "Components/CourseCatalog/CourseCatalog";
import { CourseSectionUnmapping, NewSection, SectionRequest } from "Components/CourseCatalog/CourseCatalogSubTable";
import { StrategicPlanItemRequest, StrategicPlanRequest } from "Stores/strategicPlanStore";
import { User } from "Stores/userStore";

export class EAIClient {
    endpoint: string;
    ax: AxiosInstance;
    school: string | null;

    constructor(endpoint?: string) {
        endpoint = endpoint || CONFIG.apis.eai;
        this.endpoint = endpoint || "https://api.prod.enflux.com";
        this.school = localStorage.getItem("chosen_school");
        this.ax = axios.create({
            headers: { "x-ui-location": Utils.getViewName() },
        });
        this.ax.defaults.withCredentials = true;
        this.ax.interceptors.response.use((response) => {
            // Do something with response data
            return response;
        }, function (error) {
            if (error.response && error.response.status === 401) {
                Utils.clearLocalstorage();
                location.replace(CONFIG.paths.base + "login");
            }
            // console.log("interceptor", error)
            // if (404 === error.response.status) {
            //     console.log("404 error")
            // }
            return Promise.reject(error);
        });
    }

    logIn = async (idToken: string, accessToken: string, selectedSchool: string | null, email?: string | null, subdomain?: string | null) => {
        const data: any = { id_token: idToken, access_token: accessToken, email };
        if (subdomain !== null) {
            data.subdomain = subdomain;
        }
        if (selectedSchool !== null) {
            data.school_id = selectedSchool;
        }
        return await this.ax.post(`${this.endpoint}/login`, data);
    };

    renewTokens = async () => {
        return await this.ax.post(`${this.endpoint}/renew`);
    };

    logOut = async () => {
        return await this.ax.post(`${this.endpoint}/logout`);
    };

    logLogin = async (email: string) => {
        return await this.ax.post(`${this.endpoint}/auth/log_login`, {
            email: email
        });
    };

    logBadLogin = async (email: string) => {
        return await this.ax.post(`${this.endpoint}/auth/log_bad_login`, {
            email: email
        });
    };

    generateQueryParams = (params: Record<string, string | number | boolean>) => {
        let queryParams = "";
        if (Object.keys(params).length !== 0) {
            let first = true;
            for (const property in params) {
                if (first) {
                    queryParams += "?";
                } else {
                    queryParams += "&";
                }
                queryParams += `${property}=${encodeURIComponent(params[property])}`;
                first = false;
            }
        }
        return queryParams;
    };

    // DASHBOARDS

    getDashboards = async (getAllStatus = false, excludePPC = false, grouped = true, exclude_type: string | null = null, orderBy = "group", desc = false, reloadCache = false, favorites = true) => {
        const params: any = {
            get_all_status: getAllStatus,
            exclude_ppc: excludePPC,
            grouped: grouped,
            exclude_type,
            order_by: orderBy === "group" ? "" : orderBy,
            desc,
            reload_cache: reloadCache,
            favorites
        };
        const queryParams = this.generateQueryParams(params);
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/dashboards/urls${queryParams}`);
    };

    getDashboardTemplates = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/dashboards/templates`);
    };
    getDashboardsLegacy = async () => {
        return await this.ax.get(`${this.endpoint}/dashboard/schools/${this.school}/urls`);
    };
    registerDashboard = async (dashboard: Dashboard) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/dashboards`, dashboard);
    };

    addDashboard = async (data: Partial<Dashboard>) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/dashboards`, data);
    };


    addBYOCDashboard = async (data: Partial<Dashboard>) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/dashboards/byoc`, data);
    };

    updateDashboard = async (dashboard: Partial<Dashboard>) => {
        return await this.ax.patch(`${this.endpoint}/schools/${this.school}/dashboards/${dashboard.id}`, dashboard);
    };

    updateBYOCDashboard = async (dashboard: Partial<Dashboard>) => {
        return await this.ax.patch(`${this.endpoint}/schools/${this.school}/dashboards/byoc/${dashboard.id}`, dashboard);
    };

    updateDashboardOrder = async (custom_order: dashboardOrder[]) => {
        return await this.ax.patch(`${this.endpoint}/schools/verify/dashboards/custom_order`, custom_order);
    };

    deleteDashboard = async (dashboardId: number) => {
        return this.ax.delete(`${this.endpoint}/schools/${this.school}/dashboards/${dashboardId}`);
    };

    deleteBYOCDashboard = async (dashboardId: number) => {
        return this.ax.delete(`${this.endpoint}/schools/${this.school}/dashboards/byoc/${dashboardId}`);
    };


    unregisterDashboard = async (dashboardId: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/dashboards/${dashboardId}`);
    };
    getDashboardExports = async (dashboard: Dashboard) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/dashboards/exports/${dashboard.workbook_name}`);
    };

    addFavoriteDashboard = async (dashboard_id: number) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/dashboards/favorite_dashboards`, { dashboard_id: dashboard_id });
    };

    deleteFavoriteDashBoard = async (dashboard_id: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/dashboards/favorite_dashboards/${dashboard_id}`);
    };

    actionPlanData = async (url: string): Promise<Blob> => {
        const raw = await this.ax.get(url, { responseType: "blob" });
        return new Blob([raw.data], { type: "application/pdf" });
    };

    exportAllActionPlans = async () => {
        const url = `${this.endpoint}/schools/${this.school}/actionplans/download`;
        return await this.actionPlanData(url).then((zip) => {
            saveAs(zip, "all_actionplans.zip");
        });
    };


    exportActionPlans = async (dashboard: Dashboard) => {
        const url = `${this.endpoint}/schools/${this.school}/dashboards/${dashboard.id}/download_actionplans`;
        return await this.actionPlanData(url).then((zip) => {
            saveAs(zip, `${dashboard.name.replace(/[^a-zA-Z0-9]/, "")}_actionplans.zip`);
        });
    };

    // DDD
    getDecisions = async () => {
        return await this.ax.get(`${this.endpoint}/ddd/schools/${this.school}`);
    };

    createDecision = async (dddData: any) => {
        return await this.ax.put(`${this.endpoint}/ddd/schools/${this.school}`, dddData);
    };
    // COMMON
    getCourses = async (fields?: string, tableParams?: TableParams, timestamp?: number) => {

        const params: any = {};

        if (fields) params["fields"] = fields;

        if (tableParams) {
            const { pageSize, page, sortBy, sortOrder, filters } = tableParams;

            if (timestamp) params.timestamp = timestamp;
            if (pageSize) params.page_size = pageSize;
            if (page) params.page = page;
            if (sortBy) params.sort = sortBy;
            if (sortOrder) params.sort_order = sortOrder;
            if (filters) params.filters = JSON.stringify(filters);
        }

        const queryParams = this.generateQueryParams(params);
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/courses/${queryParams}`);
    };
    getAccreditations = async () => {
        return await this.ax.get(`${this.endpoint}/ddd/schools/${this.school}/accreditation_standards`);
    };
    getCommittees = async () => {
        return await this.ax.get(`${this.endpoint}/committees/schools/${this.school}`);
    };
    getStudents = async (tableParams?: TableParams, timestamp?: number) => {
        const params = {};

        if (tableParams) {
            const { dashboardId, pageSize, page, sortBy, sortOrder, filters, include_disabled } = tableParams;

            if (timestamp) params["timestamp"] = timestamp;
            if (dashboardId) params["dashboard_id"] = dashboardId;
            if (pageSize) params["page_size"] = pageSize;
            if (page) params["page"] = page;
            if (sortBy) params["sort"] = sortBy;
            if (sortOrder) params["sort_order"] = sortOrder;
            if (filters) params["filters"] = JSON.stringify(filters);
            if (include_disabled) params["include_disabled"] = include_disabled;
        }

        const queryParams = this.generateQueryParams(params);
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/students/${queryParams}`);
    };

    createSisStudents = async (data: any) => {
        return this.ax.post(`${this.endpoint}/schools/${this.school}/students/sis-raw`, data);
    };

    getDashboardViews = async (dashboardId: number, actionplanRuuid: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/dashboards/${dashboardId}/views/${actionplanRuuid}`);
    };

    getDashboardViewById = async (viewId: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/dashboards/views/${viewId}`);
    };

    // USERS
    getCurrentUser = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/me`);
    };
    getCurrentUserPreferences = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/me/preferences`);
    };
    updateCurrentUserPreferences = async (preferences: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/users/me/preferences`, { preferences });
    };
    getUsers = async (listSize?: number, pageNumber?: number) => {
        let queryParams = "fields=basic_with_counts";
        if (listSize !== undefined && pageNumber !== undefined) queryParams += "&page_size=" + listSize + "&page=" + pageNumber;
        if (Utils.isSuperAdmin()) queryParams += "&include_internal=true";
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users?${queryParams}`);
    };

    getUsersAsync = async (tableParams?: TableParams, timestamp?: number) => {
        const params = {
            fields: "basic_with_counts",
            include_internal: false
        };

        if (tableParams) {
            const { pageSize, page, sortBy, sortOrder, filters } = tableParams;

            if (timestamp) params["timestamp"] = timestamp;
            if (pageSize) params["page_size"] = pageSize;
            if (page) params["page"] = page;
            if (sortBy) params["sort"] = sortBy;
            if (sortOrder) params["sort_order"] = sortOrder;
            if (filters) params["filters"] = JSON.stringify(filters);
        }

        const queryParams = this.generateQueryParams(params);
        return this.ax.get(`${this.endpoint}/schools/${this.school}/users${queryParams}`);
    };

    getUsersForCommittee = async (committeeId: number) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/faculty`);
    };

    getUserById = async (userID: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/` + userID);
    };

    fetchKeyAdminUser = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/key_admin`);
    };

    createUser = async (userData: any): Promise<AxiosResponse<User, any>> => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/users`, userData);
    };
    updateUser = async (userData: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/users/` + userData.id, userData);
    };
    resendUserInvite = async (id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/users/${id}/invite`, {});
    };
    generateUserInviteLink = async (userId: string): Promise<string> => {
        try {
            const response = await this.ax.get<{ invite_link: string }>(
                `${this.endpoint}/schools/${this.school}/users/${userId}/copy-invite`
            );
            const inviteLink = response.data.invite_link;
            if (!inviteLink) {
                throw new Error("Invite link not found in the response");
            }
            return inviteLink;
        } catch (error) {
            throw new Error("Unable to fetch invite link. Please try again.");
        }
    };
    async copyUserInviteLink(id: string): Promise<{ result: { invite_link: string } }> {
        try {
            const response = await this.ax.post<{ result: { invite_link: string } }>(
                `${this.endpoint}/schools/${this.school}/users/${id}/copy-invite`
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    deleteUser = async (userID: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/users/` + userID);
    };
    fetchUserOptions = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/options`);
    };
    getAssignees = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/assignees`);
    };
    setInitialPassword = async (password: string, token: string) => {
        return await this.ax.post(
            `${this.endpoint}/users/activate`,
            { password: password },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    };

    resetPassword = async (email: string) => {
        return await this.ax.post(`${this.endpoint}/users/password_reset`, { email: email });
    };

    fetchLicenseInfo = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/users/licenses`);
    };

    checkToken = async (token: string) => {
        return await this.ax.get(`${this.endpoint}/activation/${token}`);
    };

    // CQI OBSERVATIONS
    fetchObservations = async () => {
        return await this.ax.get(`${this.endpoint}/observations/schools/${this.school}`);
    };
    fetchObservationById = async (id: number) => {
        return await this.ax.get(`${this.endpoint}/observations/schools/${this.school}/${id}`);
    };
    fetchObservationsForDashboard = async (dashboard: string) => {
        return await this.ax.get(`${this.endpoint}/observations/schools/${this.school}/dashboard/${dashboard}`);
    };
    createObservation = async (data: any) => {
        return await this.ax.put(`${this.endpoint}/observations/schools/${this.school}`, data);
    };
    updateObservation = async (data: any) => {
        return await this.ax.put(`${this.endpoint}/observations/schools/${this.school}/${data.id}`, data);
    };
    deleteObservation = async (id: string) => {
        return await this.ax.delete(`${this.endpoint}/observations/schools/${this.school}/` + id);
    };


    // TICKETS
    fetchTickets = async (strategicPlanOnly: boolean, listSize?: number, pageNumber?: number) => {
        let queryParams = "fields=all";
        if (strategicPlanOnly) queryParams += "&strategic_plan_only=true";
        if (listSize !== undefined && pageNumber !== undefined) queryParams += "&page_size=" + listSize + "&page=" + pageNumber;
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans?${queryParams}`);
    };
    fetchTicketsForDashboard = async (dashboard: number, listSize?: number, pageNumber?: number, isFirstLoadOnDashboard?: boolean) => {
        let queryParams = "fields=all";
        if (isFirstLoadOnDashboard) queryParams += "&isFirstLoadOnDashboard=true";
        if (listSize !== undefined && pageNumber !== undefined) queryParams += "&page_size=" + listSize + "&page=" + pageNumber;
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/dashboard/${dashboard}?${queryParams}`);
    };
    fetchTicketById = async (id: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${id}`);
    };
    createTicket = async (data: any) => {
        data.school_id = this.school;
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/actionplans`, data);
    };
    updateTicket = async (data: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/actionplans/${data.ruuid}`, data);
    };
    deleteTicket = async (ticketId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/actionplans/${ticketId}`);
    };
    downloadTicketPDF = async (id: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/pdf/${id}`, { responseType: "blob" });
    };
    fetchCollaboratorsForTicket = async (ticketId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${ticketId}/collaborators`);
    };
    bulkChangeCollaboratorsForTicket = async (ticketId: string, data: { adds: string[], removes: string[] }) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/actionplans/${ticketId}/collaborators`, data);
    };
    addCollaboratorToTicket = async (ticketId: string, collaboratorId: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/actionplans/${ticketId}/collaborators/${collaboratorId}`);
    };
    removeCollaboratorFromTicket = async (ticketId: string, collaboratorId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/actionplans/${ticketId}/collaborators/${collaboratorId}`);
    };

    // COMMENTS
    fetchCommentsForTicket = async (id: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${id}/comments`);
    };
    addCommentToTicket = async (actionplanId: string, comment: Partial<Comment>, skipSnapshotParam = false) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/actionplans/${actionplanId}/comments${skipSnapshotParam ? "?skip_snapshot=true" : ""}`, comment);
    };
    deleteComment = async (id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/comments/${id}`);
    };
    updateComment = async ({ id, comment, formatted_comment }: {id: string, comment: string, formatted_comment: string}) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/comments/${id}`, { comment, formatted_comment });
    };

    // REPORTS
    fetchReports = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/reports`);
    };

    updateReport = async (id: string, name: string, summary: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/reports/${id}`, {
            name: name,
            summary: summary
        });
    };

    forceGenerate = async (id: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/reports/pdf/${id}?force_generate=${true}`);
    };

    createReport = async (data: any) => {
        data.school_id = this.school;
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/reports`, data);
    };

    reportUrl = async (id: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/reports/pdf/${id}`);
    };

    downloadReport = async (id: string, reportName = "report.pdf") => {
        return await this.reportData(id).then((pdf) => {
            saveAs(pdf, reportName);
        });
    };

    getActionplanReport = async (dashboard_id: string) => {
        return await this.ax({
            url: `${this.endpoint}/schools/${this.school}/dashboards/${dashboard_id}/download_actionplans`,
            method: "GET",
            responseType: "blob", // important
        });
    };

    printReport = async (id: string) => {
        return await this.reportData(id).then((pdf) => {
            const toPrint = URL.createObjectURL(pdf);
            printJS(toPrint);
        });
    };

    /*
     * Gets the report raw data. The API sends us a signed url, we simply GET
     * that url and then return it
     */
    reportData = async (id: string): Promise<Blob> => {
        const resp = await this.reportUrl(id);
        const url = resp.data.data.pdf_url;

        // the blob part is important, see here for more discussion on it
        // https://github.com/axios/axios/issues/448
        const raw = await axios.get(url, { responseType: "blob" });

        return new Blob([raw.data], { type: "application/pdf" });
    };

    // LABELS
    fetchLabels = async (grouped = false) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/labels?grouped=${grouped}`);
    };

    fetchLabel = async (labelId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/labels/${labelId}`);
    };

    createLabel = async (data) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/labels`, data);
    };

    updateLabel = async (id: number, data) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/labels/${id}`, { ...data, label_group: data.label_group, is_archived: data.is_archived });
    };

    deleteLabel = async (labelId: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/labels/${labelId}`);
    };

    fetchLabelGroups = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/labels/groups`);
    };

    createLabelGroup = async (data: { name: string, order?: number }) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/labels/groups`, data);
    };

    updateLabelGroup = async (groupId: number, data: Partial<LabelGroup>) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/labels/groups/${groupId}`, data);
    };

    deleteLabelGroup = async (groupId: number) => {
        return this.ax.delete(`${this.endpoint}/schools/${this.school}/labels/groups/${groupId}`);
    };

    // Students
    overridesStudent = async (ruuid: string, disabled: boolean) => {
        return this.ax.patch(`${this.endpoint}/schools/${this.school}/students/${ruuid}/overrides`, { disabled });
    };

    addGroupsToStudent = async (studentRuuid: string, groups: string[]) => {
        return this.ax.post(`${this.endpoint}/schools/${this.school}/students/${studentRuuid}/student_group`, { groups });
    };

    fetchGroupsForStudent = async (studentRuuid: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/students/${studentRuuid}/student_group`);
    };

    fetchStudentsForGroup = async (groupId: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/student_group/${groupId}/students`);
    };

    fetchUsersFromStudentGroup = async (groupId: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/student_group/${groupId}/users`);
    };

    createStudentGroup = async (group: Partial<StudentGroup>, students: Student[], users: string[]) => {
        const data = { ...group, students, users };
        return this.ax.post(`${this.endpoint}/schools/${this.school}/student_group/`, data);
    };

    fetchStudentGroups = async (tableParams?: TableParams, timestamp?: number) => {
        const params = {};

        if (tableParams) {
            const { pageSize, page, sortBy, sortOrder, filters } = tableParams;

            if (timestamp) params["timestamp"] = timestamp;
            if (pageSize) params["page_size"] = pageSize;
            if (page) params["page"] = page;
            if (sortBy) params["sort"] = sortBy;
            if (sortOrder) params["sort_order"] = sortOrder;
            if (filters) params["filters"] = JSON.stringify(filters);
        }
        const queryParams = this.generateQueryParams(params);

        return this.ax.get(`${this.endpoint}/schools/${this.school}/student_group/${queryParams}`);
    };

    deleteStudentGroup = async (groupId: string) => {
        return this.ax.delete(`${this.endpoint}/schools/${this.school}/student_group/${groupId}`);
    };

    updateStudentGroup = async (groupId: string, group: Partial<StudentGroup>, students: Student[], users: string[]) => {
        const data = { ...group, students, users };
        return this.ax.put(`${this.endpoint}/schools/${this.school}/student_group/${groupId}`, data);
    };

    fetchStudentDataSource = async (studentRuuid: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/student_datasource/${studentRuuid}`);
    };

    // Committees
    fetchCommittees = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/committees?extended=true`);
    };

    fetchCommittee = async (committeeId: number) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/committees/${committeeId}?extended=true`);
    };

    createCommittee = async (committeeId: string) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/committees`, { committee_name: committeeId });
    };

    updateCommittee = async (committee: Committee) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/committees/${committee.id}`, { committee_name: committee.name });
    };

    deleteCommittee = async (committeeId: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/committees/${committeeId}`);
    };

    fetchCommitteeWorkbooks = async (committeeId: number) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/workbooks`);
    };

    removeWorkbookFromCommittee = async (committeeId: number, workbookId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/workbooks/${workbookId}`);
    };

    removeDashboardFromCommittee = async (committeeId: number, dashboardId: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/dashboards/${dashboardId}`);
    };

    addWorkbookToCommittee = async (committeeId: number, workbookId: string) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/workbooks`, { workbook_id: workbookId });
    };

    addDashboardToCommittee = async (committeeId: number, dashboardId: number) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/dashboards`, { dashboard_id: dashboardId });
    };

    updateFacultiesForCommittee = async (committeeId: number, adds: string[], removes: string[]) => {
        return this.ax.put(`${this.endpoint}/schools/${this.school}/committees/${committeeId}/faculty`, {
            adds,
            removes
        });
    };

    // Embedded Resources
    fetchSchoolResources = async () => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/school_resources/`);
    };


    // MAPPING
    addMenteeToMentorMapping = async (data: any) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/mentors/`, data);
    };

    addCourseToFacultyMapping = async (data: any) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course2faculty/`, data);
    };

    addCommitteeToFacultyMapping = async (facultyId: string, data: any) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/committees/faculty/${facultyId}`, data);
    };

    // Trusted Token
    getTrustedToken = async () => {
        return await this.ax
            .get(`${this.endpoint}/token/${this.school}`)
            .then(r => { return r.data.data.token as string; });
    };

    getTrustedTokenUrl = async () => {
        return await this.ax
            .get(`${this.endpoint}/token/${this.school}/url`)
            .then(r => { return r.data.data.url as string; });
    };

    getConnectedAppToken = async () => {
        return await this.ax
            .get(`${this.endpoint}/token/${this.school}/jwt`)
            .then(r => { return r.data.data.token as string; });
    };

    // Schools
    fetchSchools = async (params?: string) => {
        return await this.ax.get(`${this.endpoint}/schools${params || ""}`);
    };

    fetchSchool = async (schoolId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${schoolId}`);
    };

    createSchool = async (data: any) => {
        return await this.ax.post(`${this.endpoint}/schools`, data);
    };

    updateSchool = async (schoolId: string, data: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${schoolId}`, data);
    };

    deleteSchool = async (schoolId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${schoolId}`);
    };

    refreshTickets = async (schoolId: string) => {
        return this.ax.post(`${this.endpoint}/super_admin/schools/${schoolId}/ticket_refresh`);
    };

    fetchIntegrations = async (schoolId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${schoolId}/integrations`);
    };

    getFilters = async (dashboard: number, params = {}, filterByValue = false) => {
        let queryParams = "";
        if (Object.keys(params).length !== 0) {
            let first = true;
            for (const property in params) {
                if (!first) queryParams += "&";
                queryParams += `${property}=${encodeURIComponent((params as any)[property])}`;
                first = false;
            }
        }
        if (filterByValue) queryParams += "&filter_by=value";
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/filters/${dashboard}/assessment_effectiveness?${queryParams}`);
    };

    // Postscript Tables
    // Terms
    listDefaultPostscriptExpressions = async () => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms/defaults`);
    };

    listPostscriptTerms = async () => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms`);
    };

    getPostscriptTerm = async (termId: number) => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms/${termId}`);
    };

    createPostscriptTerm = async (data: Partial<PostscriptTerm>) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms`, data);
    };

    updatePostscriptTerm = async (termId: number, data: Partial<PostscriptTerm>) => {
        return await this.ax.put(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms/${termId}`, data);
    };

    deletePostscriptTerm = async (termId: number) => {
        return await this.ax.delete(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms/${termId}`);
    };

    testRegex = async (term: string, expression: string, postscriptFunction: string) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/postscript/terms/simulate`, {
            term: term,
            expression: expression,
            function: postscriptFunction,
        });
    };

    // Categories
    listPostscriptCategories = async () => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/postscript/categories`);
    };

    getPostscriptCategory = async (categoryId: number) => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/postscript/categories/${categoryId}`);
    };

    createPostscriptCategory = async (data: Partial<PostscriptCategory>) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/postscript/categories`, data);
    };

    updatePostscriptCategory = async (categoryId: number, data: Partial<PostscriptCategory>) => {
        return await this.ax.put(`${this.endpoint}/super_admin/schools/${this.school}/postscript/categories/${categoryId}`, data);
    };

    deletePostscriptCategory = async (categoryId: number) => {
        return await this.ax.delete(`${this.endpoint}/super_admin/schools/${this.school}/postscript/categories/${categoryId}`);
    };

    // Notifications
    getNotificationSettings = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/notifications/`);
    };

    updateNotificationSettings = async (data: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/notifications/`, data);
    };

    updatePassingGradeSettings = async (atRiskThreshold: number) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/at_risk_threshold`, { at_risk_threshold: atRiskThreshold });
    };

    // Viewers Notifications

    getViewersNotificacion = async (actionplan_ruuid: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${actionplan_ruuid}/viewers`);
    };

    enableNotificacions = async (actionplan_ruuid: string, user_ruuid: string, notif_status: boolean) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/actionplans/${actionplan_ruuid}/viewers?enabled=${notif_status}`, {
            user_ruuid
        });
    };

    // Enflux Terms
    listEnfluxTerms = async () => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/terms`);
    };

    getEnfluxTerm = async (termId: number) => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/terms/${termId}`);
    };

    createEnfluxTerm = async (data: Partial<EnfluxTerm>) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/terms`, data);
    };

    updateEnfluxTerm = async (termId: number, data: Partial<EnfluxTerm>) => {
        return await this.ax.put(`${this.endpoint}/super_admin/schools/${this.school}/terms/${termId}`, data);
    };

    deleteEnfluxTerm = async (termId: number) => {
        return await this.ax.delete(`${this.endpoint}/super_admin/schools/${this.school}/terms/${termId}`);
    };

    // Enflux Courses
    listEnfluxCourses = async () => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/course_mappings`);
    };

       // Enflux Courses
    listPaginatedEnfluxCourses = async (pageSize: number, page: number, order: string, filters: Record<string, any>) => {
        const params = {};
        if (pageSize) params["page_size"] = pageSize;
        if (page) params["page"] = page;
        if (order) params["order"] = order;
        if (filters) params["filters"] = JSON.stringify(filters);
        const queryParams = this.generateQueryParams(params);
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/course_mappings${queryParams}`);
    };

    getCourseMapping = async (enfluxCourseId: number) => {
        return await this.ax.get(`${this.endpoint}/super_admin/schools/${this.school}/course_mappings/${enfluxCourseId}`);
    };

    createUserOverride = async (data: any) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/course_mappings/override`, data);
    };

    updateUserOverride = async (userOverrideId: number, data: any) => {
        return await this.ax.put(`${this.endpoint}/super_admin/schools/${this.school}/course_mappings/override/${userOverrideId}`, data);
    };

    getSettings = async (setting?: string) => {
        const url = setting ? `/${setting}` : "";
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/settings${url}`);
    };

    updateSettings = async (setting: string, data: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/settings/${setting}`, data);
    };

    getAcceptedMimetypes = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/attachments/mimetypes`);
    };

    createAndUploadAttachments = async (files: AttachmentFile[]): Promise<string[]> => {
        return await Promise.all(files.map(async file => {
            const resp = await this.ax.post(`${this.endpoint}/schools/${this.school}/attachments`, {
                file_name: file.sanitizedName,
                file_type: file.type,
                file_size_bytes: file.size,
            });
            const respData = resp.data.data;
            const id: string = respData.id;
            const fields = respData.upload_data.fields;
            const formData = new FormData();
            for (const k in fields) {
                formData.append(k, fields[k]);
            }
            formData.append("Content-Type", file.type);
            formData.append("x-amz-meta-original-name", file.sanitizedName);
            formData.append("file", file);
            await axios({
                method: "POST",
                url: respData.upload_data.url,
                data: formData,
            });
            return id;
        }));
    };

    getAllAttachmentsForActionplan = async (actionplanId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${actionplanId}/attachments`);
    };

    getAllHistoryForActionPlan = async (actionPlanId: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/actionplans/${actionPlanId}/history`);
    };

    addMilestoneToActionPlan = async (actionPlan: string, description: string, dueDate: Date | undefined | null) => {
        return this.ax.post(`${this.endpoint}/schools/${this.school}/milestones/`, {
            actionplan_id: actionPlan,
            description,
            due_date: dueDate
        });
    };

    getAllMilestonesForActionPlan = async (actionPlanId: string) => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/milestones/${actionPlanId}`);
    };

    updateMilestoneForActionPlan = async (actionPlanId: string, ruuid: string, status: string, description: string, dueDate: string | undefined | null) => {
        return this.ax.put(`${this.endpoint}/schools/${this.school}/milestones/${actionPlanId}/${ruuid}`, {
            status,
            description,
            due_date: dueDate
        });
    };

    updateStatusMilestoneForActionPlan = async (actionPlanId: string, ruuid, status: string) => {
        return this.ax.put(`${this.endpoint}/schools/${this.school}/milestones/${actionPlanId}/${ruuid}`, {
            status
        });
    };

    deleteMilestoneForActionPlan = async (actionPlanId: string, ruuid: string) => {
        return this.ax.delete(`${this.endpoint}/schools/${this.school}/milestones/${actionPlanId}/${ruuid}`);
    };

    getAttachmentDownloadLink = async (attachmentId: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/attachments/${attachmentId}`);
    };

    addAttachmentToActionplan = async (actionplanId: string, attachmentIds: string[]) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/actionplans/${actionplanId}/attachments`, { attachment_ids: attachmentIds });
    };

    deleteAttachment = async (attachmentId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/attachments/${attachmentId}`);
    };

    deleteAttachmentFromActionplan = async (actionplanId: string, attachmentId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/actionplans/${actionplanId}/attachments/${attachmentId}`);
    };

    deleteAttachmentFromComment = async (commentId: string, attachmentId: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/comments/${commentId}/attachments/${attachmentId}`);
    };

    startMasquerade = async (email: string) => {
        return await this.ax.post(`${this.endpoint}/super_admin/masquerade/start`, { email: email });
    };

    stopMasquerade = async () => {
        return await this.ax.post(`${this.endpoint}/super_admin/masquerade/stop`);
    };

    fetchConfigurableDatasources = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/datasources?configurable=true`);
    };

    fetchDatasourceTemplates = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/datasources/templates`);
    };

    checkDatasourceExists = async (datasourceName: string, school?: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${school || this.school}/datasources/${datasourceName}?exists=true`);
    };

    fetchDatasource = async (datasourceName: string, school?: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${school || this.school}/datasources/${datasourceName}`);
    };

    fetchDatasources = async (school?: string) => {
        return await this.ax.get(`${this.endpoint}/schools/${school || this.school}/datasources`);
    };

    updateCanvasCode = async (code: string) => {
        if (!this.school) return;
        return await this.updateDatasource(this.school, "canvas", {
            settings: {
                code: code
            }
        });
    };

    updateCanvasDatasource = async (data: any) => {
        if (!this.school) return;
        return await this.updateDatasource(this.school, "canvas", data);
    };

    updateDatasource = async (schoolId: string, datasourceName: string, data: any) => {
        return await this.ax.put(`${this.endpoint}/schools/${schoolId}/datasources/${datasourceName}`, data);
    };

    deleteDatasource = async (schoolId: string, datasourceName: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${schoolId}/datasources/${datasourceName}`);
    };

    saveScheduledIngestion = async (schoolId: string, ingestDate: Date, comment: string, fullIngestion: boolean) => {
        return await this.ax.post(`${this.endpoint}/schools/${schoolId}/scheduled_ingestions/`, {
            ingest_date: ingestDate,
            comment,
            full_ingestion: fullIngestion
        });
    };

    updateScheduledIngestion = async (schoolId: string, scheduledIngestionId: number, ingestDate: Date, comment: string, fullIngestion: boolean) => {
        return await this.ax.put(
            `${this.endpoint}/schools/${schoolId}/scheduled_ingestions/${scheduledIngestionId}`,
            { ingest_date: ingestDate, comment, full_ingestion: fullIngestion });
    };

    deleteScheduledIngestion = async (schoolId: string, scheduledIngestionId: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${schoolId}/scheduled_ingestions/${scheduledIngestionId}`);
    };

    getUniversityUrl = async (email: string) => {
        return await this.ax.get(`${this.endpoint}/auth/subdomain/email/${email}`);
    };

    getAuthMethod = async (path: string) => {
        return await this.ax.get(`${this.endpoint}/auth/method/path/${path}`);
    };

    // BANNERS
    fetchBanners = async () => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/banners/`);
    };

    fetchWhatsNewBanner = async () => {
        return this.ax.get(`${this.endpoint}/schools/${this.school}/banners/whats-new`);
    };

    fetchBannersManagement = async (tableParams: TableParams) => {
        const { pageSize, page, sortBy, sortOrder, filters } = tableParams;
        const params = {};
        if (pageSize) params["page_size"] = pageSize;
        if (page) params["page"] = page;
        if (sortBy) params["sort"] = sortBy;
        if (sortOrder) params["sort_order"] = sortOrder;
        if (filters) params["filters"] = JSON.stringify(filters);
        const queryParams = this.generateQueryParams(params);
        return this.ax.get(`${this.endpoint}/super_admin/banners${queryParams}`);
    };

    fetchBannerSchools = async (ruuid) => {
        return this.ax.get(`${this.endpoint}/super_admin/banners/${ruuid}/schools`);
    };

    deleteBanner = async (ruuid: string) => {
        return this.ax.delete(`${this.endpoint}/super_admin/banners/${ruuid}`);
    };

    updateBanner = async (ruuid: string, banner: Banner, schools: string[], schoolTypes: string[], dataSources: string[]) => {
        return this.ax.put(`${this.endpoint}/super_admin/banners/${ruuid}`, {
            ...banner,
            schools,
            school_types: schoolTypes,
            data_sources: dataSources,
        });
    };

    addBanner = async (banner: Banner, schools: string[], schoolTypes: string[], dataSources: string[]) => {
        return this.ax.post(`${this.endpoint}/super_admin/banners`, {
            ...banner,
            schools,
            school_types: schoolTypes,
            data_sources: dataSources,
        });
    };

    likeBanner = async (ruuid: string) => {
        return this.ax.post(`${this.endpoint}/schools/${this.school}/banners/${ruuid}/likes`);
    };

    fetchBannersBySubdomain = async (subdomain) => {
        return this.ax.get(`${this.endpoint}/subdomain/${subdomain}/banners`);
    };

    // Feature Gating
    fetchFeatureGatingList = async (schoolId?: string, reset = false) => {
        const school = schoolId || this.school;
        const queryParams = this.generateQueryParams({ reset });

        return this.ax.get(`${this.endpoint}/schools/${school}/features/${queryParams}`);
    };

    fetchFeatures = async (tableParams: FeaturesTableParams) => {
        const params: Record<string, string | number | boolean> = {
            page_size: tableParams.pageSize,
            page: tableParams.page
        };
        const queryParams = this.generateQueryParams(params);
        return this.ax.get(`${this.endpoint}/features/${queryParams}`);
    };

    fetchSchoolFeatures = async (schoolId: string, tableParams: FeaturesTableParams) => {
        const params: Record<string, string | number | boolean> = {
            page_size: tableParams.pageSize,
            page: tableParams.page
        };
        const queryParams = this.generateQueryParams(params);
        return this.ax.get(`${this.endpoint}/schools/${schoolId}/school_features/${queryParams}`);
    };

    updateFeature = async (ruuid: string, enabled: boolean, allSchools: boolean, description: string) => {
        return this.ax.put(`${this.endpoint}/features/${ruuid}`, { enabled, all_schools: allSchools, description });
    };

    deleteSchoolFeature = async (schoolId: string, component: string, name: string) => {
        return this.ax.post(`${this.endpoint}/schools/${schoolId}/school_features/reset`, {
            component,
            name
        });
    };

    enableSchoolFeature = async (schoolId: string, component: string, name: string, enabled: boolean) => {
        return this.ax.post(`${this.endpoint}/schools/${schoolId}/school_features/enabled`, {
            component, name, enabled
        });
    };

    getDomainWhitelist = async () => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/whitelist`);
    };

    //  Course Catalog

    getCourseCatalog = async (): Promise<CoursesCatalogResponse> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/course_catalog/`);
    };

    getSuggestedCourses = async (sectionsId: SectionsIds): Promise<CoursesCatalogResponse> => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course_catalog/suggested/`, sectionsId);
    };

    getUnmmapedSections = async (page?: number, name_search?: string, pageSize?: number): Promise<SectionsCatalogResponse> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/course_catalog/unmapped_sections/?page_size=${pageSize ?? 10}&page=${page}&name_search=${name_search}`);
    };

    updateCourseCatalog = async (course: EditCourseRequest) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/course_catalog/${course.rid}/`, {
            course_title: course.course_title,
            subject_number: course.subject_number,
            curriculum_year: course.curriculum_year,
            credit_hours: course.credit_hours,
            status: course.status,
            last_term_id: course.last_term_id,
            update_comment: course.update_comment,
        });
    };

    createCourse = async (newCourse: CreateCourseRequest) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course_catalog/`, newCourse);
    };

    addCourseDescription = async (course_rid: string, description: CourseDescription) => {
        return await this.ax.patch(`${this.endpoint}/schools/${this.school}/course_catalog/${course_rid}/description`, description);
    };

    archiveCourse = async (status: CourseStatus, last_term_id: number, update_comment: string, course_rid: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/course_catalog/${course_rid}/`, {
            status,
            last_term_id,
            update_comment
        });
    };

    mapSections = async (sections: Array<number>, course: number) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course_catalog/map_section/`, {
            enflux_courses: sections,
            enflux_catalog_id: course
        });
    };

    updateSectionCatalog = async (section: SectionRequest, rid: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/course_catalog/section/${rid}/`, section);
    };

    createSection = async (section: NewSection) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course_catalog/section/`, section);
    };

    createTerm = async (section: NewSection) => {
        return await this.ax.post(`${this.endpoint}/super_admin/schools/${this.school}/terms`, section);
    };

    unmapCourse = async (unmapping: CourseSectionUnmapping) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/course_catalog/unmap_section/`, unmapping);
    };

    crossCourses = async (course_id: number, courses_related) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/course_catalog/crossed/${course_id}/`, courses_related);
    };

    updateDashboardThumbnail = async (workbook: string, formData: FormData) => {
        return this.ax.post(`${this.endpoint}/schools/${this.school}/dashboards/${workbook}/thumbnail`, formData);
    };

    // Curriculum mapping

    createCurriculumMapping = async (new_curriculum_mapping: CurriculumMapping) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/curriculum_mapping/`, new_curriculum_mapping);
    };

    updateCurriculumMapping = async (curriculum_mapping: CurriculumMapping, curriculum_mapping_id: string | number) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/${curriculum_mapping_id}`, curriculum_mapping);
    };

    deleteCurriculumMapping = async (curriculum_mapping_id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/curriculum_mapping/${curriculum_mapping_id}`);
    };

    createCognitiveLevel = async (new_cognitive_level: CustomOptionType) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/curriculum_mapping/cognitive_level`, new_cognitive_level);
    };

    getCognitiveLevel = async (): Promise<Array<CustomOptionType>> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/curriculum_mapping/cognitive_level`);
    };

    updateCognitiveLevel = async (cognitive_level: CustomOptionType, cognitive_level_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/cognitive_level/${cognitive_level_id}`, cognitive_level);
    };

    deleteCognitiveLevel = async (cognitive_level_id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/curriculum_mapping/cognitive_level/${cognitive_level_id}`);
    };

    createCLO = async (new_clo: CustomOptionType) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/curriculum_mapping/curriculum_mapping_clos`, new_clo);
    };

    getCLOs = async (): Promise<Array<CustomOptionType>> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/curriculum_mapping/curriculum_mapping_clos`);
    };

    updateCLO = async (clo: CustomOptionType, clo_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/curriculum_mapping_clos/${clo_id}`, clo);
    };

    deleteCLO = async (clo_id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/curriculum_mapping/curriculum_mapping_clos/${clo_id}`);
    };

    createDomainCompetency = async (new_domain_competency: CustomDomainCompetency) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency`, new_domain_competency);
    };

    getDomainCompetencies = async (): Promise<Array<DomainCompetency>> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency`);
    };

    updateDomainCompetency = async (domain_competency: DomainCompetency, domain_competency_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency/${domain_competency_id}`, domain_competency);
    };

    toggleDomainCompetency = async (enabled: boolean, domain_competency_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency/${domain_competency_id}`, {enabled});
    };

    updateStandardCompetency = async (standard_competency: DomainCompetency, standard_competency_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/standard_domain_competency/${standard_competency_id}`, standard_competency);
    };

    uploadDomainCompetency = async (formData: FormData) => {
        return await this.ax.post(
            `${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency/upload`,
            formData
        );
    };

    uploadCurriculumMapping = async (formData: FormData) => {
        return await this.ax.post(
            `${this.endpoint}/schools/${this.school}/curriculum_mapping/upload`,
            formData
        );
    };

    deleteDomainCompetency = async (domain_competency_id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/curriculum_mapping/domain_competency/${domain_competency_id}`);
    };

    createTeachingMethod = async (new_teaching_method: CustomOptionType) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/curriculum_mapping/teaching_method`, new_teaching_method);
    };

    getTeachingMethods = async (): Promise<Array<CustomOptionType>> => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/curriculum_mapping/teaching_method`);
    };

    updateTeachingMethod = async (teaching_method: CustomOptionType, teaching_method_id: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/curriculum_mapping/teaching_method/${teaching_method_id}`, teaching_method);
    };

    deleteTeachingMethod = async (teaching_method_id: string) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/curriculum_mapping/teaching_method/${teaching_method_id}`);
    };

    // Strategic plans
    // Institutional Effectiveness plans

    getStrategicPlans = async (flatten: boolean, items_only = false) => {
        return await this.ax.get(`${this.endpoint}/schools/${this.school}/strategicplan?flatten=${flatten}&items_only=${items_only}`);
    };

    createStrategicPlan = async (strategicPlan: StrategicPlanRequest) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/strategicplan`, strategicPlan);
    };

    createStrategicPlanItem = async (strategic_plan_id: string, item: StrategicPlanItemRequest) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/strategicplan/${strategic_plan_id}/item`, item);
    };

    updateStrategicPlan = async (strategic_plan_id: string, strategic_plan: StrategicPlanRequest) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/strategicplan/${strategic_plan_id}`, strategic_plan);
    };
    updateStrategicPlanItem = async (strategic_plan_ruuid: string, strategic_plan_id: string, strategic_plan: Partial<StrategicPlanItemRequest>) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/strategicplan/${strategic_plan_ruuid}/item/${strategic_plan_id}`, strategic_plan);
    };

    archiveStrategicPlanItem = async (id: number, status: string, strategic_plan_ruuid: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/strategicplan/${strategic_plan_ruuid}/item/${id}`,{
            status
        });
    };

    archiveStrategicPlan = async (status: string, strategic_plan_ruuid: string) => {
        return await this.ax.put(`${this.endpoint}/schools/${this.school}/strategicplan/${strategic_plan_ruuid}`,{
            status
        });
    };

    _deleteStrategicPlanItem = async (id: number) => {
        return await this.ax.delete(`${this.endpoint}/schools/${this.school}/strategicplan/item/${id}`);
    };

    copyStrategicPlan = async (name: string, strategic_plan_ruuid: string) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/strategicplan/copy/${strategic_plan_ruuid}`, { name });
    };

    // Categorizer
    sendCategorizedQuestionFeedback = async (ruuid: string, thumbs_up: boolean, category?: string, comment?: string, old_subcompetency?: string) => {
        return await this.ax.post(`${this.endpoint}/schools/${this.school}/categorized_questions/${ruuid}/feedback`, {
            reported_verified: thumbs_up,
            reported_category: category,
            reported_comment: comment,
            old_subcompetency: old_subcompetency
        });
    };

    enableEnfluxMLModel = async (schoolId: string, modelId: string, enabled: boolean) => {
        return await this.ax.post(`${this.endpoint}/schools/${schoolId}/categorizer_models/${modelId}/enabled`, { enabled });
    };

    // VersionInfo envDisplayer.tsx endpoints fetcher
    getEnvConfig = async () => {
        const res = await this.ax.get(`${this.endpoint}/super_admin/config`);
        return res.data?.data;
    };

    getApiStage = async () => {
        const res = await this.ax.get(`${this.endpoint}/super_admin/get_stage`);
        return res.data?.api_stage || "N/A";
    };

    getGitBranch = async () => {
        const res = await this.ax.get(`${this.endpoint}/super_admin/get_current_branch`);
        return res.data?.branch || "N/A";
    };

    getMigrationVersion = async () => {
        const res = await this.ax.get(`${this.endpoint}/super_admin/get_migration`);
        return res.data?.revision || "N/A";
    };
}
